import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, CheckCircle2, Phone, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CustomCursor from '../components/CustomCursor';
import { useLanguage } from '../context/LanguageContext';

const serviceImages = [
  'https://images.unsplash.com/photo-1682888813789-c39fe30921e2?w=800&q=80',
  'https://images.unsplash.com/photo-1721523262910-c438933f673f?w=800&q=80',
  'https://images.unsplash.com/photo-1765959617888-30837a158667?w=800&q=80',
  'https://images.unsplash.com/photo-1768434118991-667871772318?w=800&q=80',
  'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800&q=80',
  'https://images.unsplash.com/photo-1597406462800-280ba190eb4c?w=800&q=80',
  'https://images.unsplash.com/photo-1760072513367-55182245e76c?w=800&q=80',
  'https://images.unsplash.com/photo-1758998202918-d921125a700f?w=800&q=80',
  'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
  'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&q=80',
];

const ServiciosPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#0A0A0A] min-h-screen">
      <CustomCursor />
      <Navbar />
      
      {/* Hero Section */}
      <HeroSection />
      
      {/* Services Grid */}
      <ServicesGrid />
      
      {/* Process Section */}
      <ProcessSection />
      
      {/* CTA Section */}
      <CTASection />
      
      <Footer />
    </div>
  );
};

const HeroSection = () => {
  const { t } = useLanguage();
  
  return (
    <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1920&q=80"
          alt="Premium Remodeling"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0A0A0A]" />
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center pt-32">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="font-montserrat font-medium text-xs tracking-[0.3em] text-[#C5A55C] uppercase block mb-6"
        >
          {t('servicesPage.heroTagline')}
        </motion.span>
        
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-montserrat font-light text-4xl md:text-6xl lg:text-7xl text-white tracking-[0.05em] uppercase mb-6"
        >
          {t('servicesPage.heroTitle1')}
          <br />
          <span className="text-[#C5A55C]">{t('servicesPage.heroTitle2')}</span> {t('servicesPage.heroTitleSuffix')}
        </motion.h1>
        
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="font-raleway font-light text-lg md:text-xl text-white/70 max-w-3xl mx-auto mb-12 tracking-wide"
        >
          {t('servicesPage.heroDescription')}
        </motion.p>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col sm:flex-row gap-4 justify-center"
        >
          <Link
            to="/contacto"
            className="group px-10 py-4 bg-[#C5A55C] text-white hover:bg-white hover:text-[#1A1A1A] transition-all duration-500 font-montserrat font-medium text-sm tracking-[0.2em] uppercase flex items-center justify-center gap-3"
            data-testid="hero-cta-consultation"
          >
            {t('servicesPage.ctaConsultation')}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
          
          <a
            href="#services"
            className="px-10 py-4 border-2 border-white/30 text-white hover:border-[#C5A55C] hover:text-[#C5A55C] transition-all duration-500 font-montserrat font-medium text-sm tracking-[0.2em] uppercase"
            data-testid="hero-cta-services"
          >
            {t('servicesPage.ctaViewServices')}
          </a>
        </motion.div>
      </div>
      
      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-[1px] h-16 bg-gradient-to-b from-[#C5A55C] to-transparent" />
      </motion.div>
    </section>
  );
};

const ServicesGrid = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const { t } = useLanguage();
  const services = t('servicesPage.services');
  
  return (
    <section id="services" ref={ref} className="py-32 bg-gradient-to-b from-[#0A0A0A] to-[#0F0F0F]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="text-center mb-20"
        >
          <span className="font-montserrat font-medium text-xs tracking-[0.3em] text-[#C5A55C] uppercase block mb-6">
            {t('servicesPage.servicesTagline')}
          </span>
          <h2 className="font-montserrat font-light text-4xl md:text-5xl text-white tracking-[0.08em] uppercase mb-6">
            {t('servicesPage.servicesTitle')}
          </h2>
          <div className="w-24 h-[2px] bg-[#C5A55C] mx-auto" />
        </motion.div>
        
        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <ServiceCard key={index} service={service} index={index} inView={inView} image={serviceImages[index]} />
          ))}
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ service, index, inView, image }) => {
  const { t } = useLanguage();
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      className="group relative bg-[#1A1A1A] overflow-hidden"
      data-testid={`service-card-${index}`}
    >
      {/* Image */}
      <div className="relative h-64 overflow-hidden">
        <img
          src={image}
          alt={service.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-transparent to-transparent" />
      </div>
      
      {/* Content */}
      <div className="p-8">
        <h3 className="font-montserrat font-medium text-xl text-white mb-4 group-hover:text-[#C5A55C] transition-colors duration-300">
          {service.title}
        </h3>
        <p className="font-raleway font-light text-sm text-white/60 leading-relaxed mb-6">
          {service.description}
        </p>
        <Link
          to="/contacto"
          className="inline-flex items-center gap-2 font-montserrat font-medium text-xs tracking-[0.15em] text-[#C5A55C] uppercase group-hover:gap-3 transition-all duration-300"
        >
          {t('servicesPage.learnMore')}
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
      
      {/* Hover border */}
      <div className="absolute inset-0 border-2 border-transparent group-hover:border-[#C5A55C]/50 transition-all duration-500 pointer-events-none" />
    </motion.div>
  );
};

const ProcessSection = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const { t } = useLanguage();
  const processSteps = t('servicesPage.processSteps');
  
  return (
    <section ref={ref} className="py-32 bg-gradient-to-b from-[#F8F6F1] to-[#EFECE5] relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C5A55C] to-transparent" />
      <div className="absolute top-12 left-12 w-20 h-20 border-l-2 border-t-2 border-[#C5A55C]/30" />
      <div className="absolute top-12 right-12 w-20 h-20 border-r-2 border-t-2 border-[#C5A55C]/30" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
          className="text-center mb-20"
        >
          <span className="font-montserrat font-medium text-xs tracking-[0.3em] text-[#C5A55C] uppercase block mb-6">
            {t('servicesPage.processTagline')}
          </span>
          <h2 className="font-montserrat font-light text-4xl md:text-5xl text-[#1A1A1A] tracking-[0.08em] uppercase mb-6">
            {t('servicesPage.processTitle')}
          </h2>
          <div className="w-24 h-[2px] bg-[#C5A55C] mx-auto" />
        </motion.div>
        
        {/* Process Steps */}
        <div className="relative">
          {/* Connecting line */}
          <div className="absolute top-1/2 left-0 right-0 h-[2px] bg-[#C5A55C]/20 hidden lg:block" />
          
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {processSteps.map((step, index) => (
              <ProcessStep key={index} step={step} index={index} inView={inView} />
            ))}
          </div>
        </div>
      </div>
      
      <div className="absolute bottom-12 left-12 w-20 h-20 border-l-2 border-b-2 border-[#C5A55C]/30" />
      <div className="absolute bottom-12 right-12 w-20 h-20 border-r-2 border-b-2 border-[#C5A55C]/30" />
    </section>
  );
};

const ProcessStep = ({ step, index, inView }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay: index * 0.15 }}
      className="relative text-center"
    >
      {/* Step number */}
      <div className="relative z-10 w-20 h-20 mx-auto mb-6 bg-white shadow-xl flex items-center justify-center border border-[#C5A55C]/20">
        <span className="font-montserrat font-light text-3xl text-[#C5A55C]">{step.step}</span>
      </div>
      
      {/* Content */}
      <h3 className="font-montserrat font-medium text-lg text-[#1A1A1A] mb-3">
        {step.title}
      </h3>
      <p className="font-raleway font-light text-sm text-[#1A1A1A]/60 leading-relaxed">
        {step.description}
      </p>
    </motion.div>
  );
};

const CTASection = () => {
  const { ref, inView } = useInView({ threshold: 0.1, triggerOnce: true });
  const { t } = useLanguage();
  
  return (
    <section ref={ref} className="py-32 bg-[#1A1A1A] relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23C5A55C\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
      </div>
      
      {/* Gold accent lines */}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C5A55C] to-transparent" />
      
      <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1 }}
        >
          <span className="font-montserrat font-medium text-xs tracking-[0.3em] text-[#C5A55C] uppercase block mb-6">
            {t('servicesPage.ctaTagline')}
          </span>
          <h2 className="font-montserrat font-light text-4xl md:text-5xl lg:text-6xl text-white tracking-[0.08em] uppercase mb-8">
            {t('servicesPage.ctaTitle1')}
            <br />
            <span className="text-[#C5A55C]">{t('servicesPage.ctaTitle2')}</span>
          </h2>
          <p className="font-raleway font-light text-lg text-white/60 max-w-2xl mx-auto mb-12">
            {t('servicesPage.ctaDescription')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contacto"
              className="group px-12 py-5 bg-[#C5A55C] text-white hover:bg-white hover:text-[#1A1A1A] transition-all duration-500 font-montserrat font-medium text-sm tracking-[0.2em] uppercase flex items-center justify-center gap-3"
              data-testid="cta-book-consultation"
            >
              {t('servicesPage.ctaButton')}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
            
            <a
              href="tel:+12155550123"
              className="px-12 py-5 border-2 border-white/30 text-white hover:border-[#C5A55C] hover:text-[#C5A55C] transition-all duration-500 font-montserrat font-medium text-sm tracking-[0.2em] uppercase flex items-center justify-center gap-3"
            >
              <Phone className="w-4 h-4" />
              (215) 555-0123
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiciosPage;
